import { useParams } from "react-router-dom";
import moment from "moment";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import NumberFormat from "../../../components/NumberFormat";
import CardForm from "../../../components/CardForm";
import TableListTitle from "../../../components/TableList/TableListTitle";
import {
  Invoice,
  InvoiceItem,
  InvoiceStatus,
  SetttingGroup,
  useAllGroupSettingsQuery,
  useCancelInvoiceMutation,
  useGetInvoiceQuery,
  usePayInvoiceMutation,
} from "../../../generated/graphql";
import { BlobProvider } from "@react-pdf/renderer";
import PrintInvoice from "../PrintInvoice";
import { Fragment, useEffect, useMemo, useState } from "react";
import DetailLine from "../../../components/DetailLine";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  getInvoiceStatusColorScheme,
  getInvoiceStatusName,
} from "../../../helpers/global";
import RouterLink from "../../../components/RouterLink";
import { getMenusWithDivider } from "../../../helpers/menu";
import { useConfirmation } from "../../../contexts/ConfirmationContext";
import { createColumnHelper } from "@tanstack/react-table";
import DefaultTable from "../../../components/DefaultTable/DefaultTable";
import ModalFormPay from "../../../components/ModalFormPay";
import { IModalFormPay } from "../../../components/ModalFormPay/interface";
import update from "immutability-helper";
import InvoiceForm from "../InvoiceForm";
import { replaceNewlinesWithBr } from "../../../helpers/elements";
import { FormMutationType } from "../../../components/FullWidthModal/interface";

const columnHelper = createColumnHelper<InvoiceItem>();

const InvoiceDetail = () => {
  const { invoiceId } = useParams();

  const { data, loading, refetch } = useGetInvoiceQuery({
    variables: { id: invoiceId as string },
  });

  const { data: dataSettings } = useAllGroupSettingsQuery({
    variables: { group: SetttingGroup.InvoiceSetting },
  });

  const [invoice, setInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    setInvoice((data?.getInvoice as Invoice) || null);
  }, [data]);

  const [cancelInvoice] = useCancelInvoiceMutation();

  const onCancel = () => {
    confirmation({
      description: "Are you sure want to cancel this invoice?",
      callback: async () => {
        try {
          setIsLoading(true);
          const res = await cancelInvoice({
            variables: {
              id: invoiceId as string,
            },
          });
          setInvoice(
            update(invoice, {
              status: { $set: res.data?.cancelInvoice.status as InvoiceStatus },
              canceledAt: { $set: res.data?.cancelInvoice.canceledAt },
            })
          );
        } catch (e) {
        } finally {
          setIsLoading(false);
          close();
        }
      },
    });
  };

  const {
    isOpen: isOpenPay,
    onClose: onClosePay,
    onOpen: onOpenPay,
  } = useDisclosure();

  const {
    isOpen: isOpenForm,
    onOpen: onOpenForm,
    onClose: onCloseForm,
  } = useDisclosure();

  const [payInvoiceMutation] = usePayInvoiceMutation();

  const onPay = async (inputData: IModalFormPay) => {
    confirmation({
      title: "Confirmation",
      description: (
        <Box>
          Invoice with customer{" "}
          <Box as="span" fontWeight="bold">
            {invoice?.customerName}
          </Box>{" "}
          will be paid. Continue?
        </Box>
      ),
      callback: async () => {
        setIsLoading(true);
        const res = await payInvoiceMutation({
          variables: { id: invoice?.id as string, input: inputData },
        });
        setInvoice(
          update(invoice, {
            status: { $set: res.data?.payInvoice.status as InvoiceStatus },
            paidAt: { $set: res.data?.payInvoice.paidAt },
          })
        );
        setIsLoading(false);
        close();
        onClosePay();
      },
    });
  };

  const menuPrint = useMemo(
    () => (
      <BlobProvider
        document={
          <PrintInvoice
            invoice={invoice as Invoice}
            settings={dataSettings?.allGroupSettings}
          />
        }
      >
        {({ url }) => (
          <MenuItem as={Link} href={url || ""} target="_blank" size="xs">
            Print
          </MenuItem>
        )}
      </BlobProvider>
    ),
    [dataSettings, invoice]
  );

  const actionMenuItems = () => {
    const menus = [];

    if (data) {
      menus.push(menuPrint);
    }

    if (invoice?.status === InvoiceStatus.Unpaid) {
      menus.push(<MenuItem onClick={onOpenForm}>Edit</MenuItem>);
      menus.push(<MenuItem onClick={onOpenPay}>Pay</MenuItem>);
    }

    if (invoice?.status !== InvoiceStatus.Canceled) {
      menus.push(<MenuItem onClick={onCancel}>Cancel</MenuItem>);
    }

    return getMenusWithDivider(menus);
  };

  const { confirmation, setIsLoading, close } = useConfirmation();

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "number",
        header: () => "No.",
        cell: (info) => info.row.index + 1,
      }),
      columnHelper.accessor("itemName", {
        header: () => "Item Name",
        cell: (info) => replaceNewlinesWithBr(info.getValue()),
      }),
      columnHelper.accessor("qty", {
        header: () => "Qty",
        cell: (info) => <NumberFormat value={info.getValue()} />,
      }),
      columnHelper.accessor("price", {
        header: () => "Price",
        cell: (info) => <NumberFormat prefix="$ " value={info.getValue()} />,
      }),
      columnHelper.display({
        id: "total",
        header: () => "Total",
        cell: (info) => (
          <NumberFormat
            prefix="$ "
            value={info.row.original.price * info.row.original.qty}
          />
        ),
      }),
    ];
  }, []);

  if (loading) {
    return (
      <Flex alignItems="center" justifyContent="center" minH="200px" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return (
    <>
      <CardForm maxW="container.xl">
        <TableListTitle
          title={`Detail Invoice #${invoice?.invoiceNumber}`}
          subtitleComponent={
            <Box
              color="#717A7A"
              fontWeight="500"
              fontSize="14px"
              lineHeight="1.6"
              mt={2}
              alignItems="center"
            >
              <Box mb={1}>
                Status:{" "}
                <Badge
                  colorScheme={getInvoiceStatusColorScheme(
                    invoice?.status as InvoiceStatus
                  )}
                >
                  {getInvoiceStatusName(invoice?.status as InvoiceStatus)}
                </Badge>
              </Box>
            </Box>
          }
          actionComponents={
            <Flex gap={5}>
              <Button as={RouterLink} size="sm" variant="link" to="/invoice">
                Back
              </Button>
              <Menu placement="bottom-end">
                <MenuButton
                  size="sm"
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                >
                  Action
                </MenuButton>
                <MenuList>
                  {actionMenuItems().map((elm, index) => (
                    <Fragment key={`action-${index}`}>{elm}</Fragment>
                  ))}
                </MenuList>
              </Menu>
            </Flex>
          }
        />

        <Box>
          <DetailLine
            title="Invoice Information"
            mb={10}
            items={[
              {
                label: "Invoice Date",
                value: moment(invoice?.invoiceDate).format("DD MMMM, YYYY"),
              },
              {
                label: "Invoice Number",
                value: invoice?.invoiceNumber
                  ? `#${invoice?.invoiceNumber}`
                  : "",
              },
              {
                label: "Ref No.",
                value: invoice?.refNo ?? "-",
              },
              {
                label: "Total",
                value: <NumberFormat value={invoice?.total} />,
              },
              {
                label: "Payment Type",
                value: invoice?.paymentType || "-",
              },
              {
                label: "Paid at",
                value: invoice?.paidAt
                  ? moment(invoice?.paidAt).format("DD MMMM, YYYY")
                  : "-",
              },
              {
                label: "Canceled at",
                value: invoice?.canceledAt
                  ? moment(invoice?.canceledAt).format("DD MMMM, YYYY")
                  : "-",
              },
            ]}
          />

          <DetailLine
            title="Customer Information"
            mb={10}
            items={[
              {
                label: "Customer Name",
                value: invoice?.customerName || "-",
              },
              {
                label: "Customer Signature Name",
                value: invoice?.customerSignatureName || "-",
              },
              {
                label: "Booker Name",
                value: invoice?.bookerName || "-",
              },
            ]}
          />

          <DetailLine
            title="Signature Info"
            items={[
              {
                label: "Signature",
                value: invoice?.signature?.name || "",
              },
              {
                label: "Image",
                value: (
                  <Image src={invoice?.signature?.imageUrl} w="auto" h="80px" />
                ),
              },
              {
                label: "Signature Background",
                value: invoice?.signatureBackground?.name || "",
              },
              {
                label: "Image",
                value: (
                  <Image
                    src={invoice?.signatureBackground?.imageUrl}
                    w="auto"
                    h="80px"
                  />
                ),
              },
            ]}
          />

          <DetailLine
            title="More Info"
            items={[
              {
                label: "Payment Details: Company Name",
                value: invoice?.paymentDetail?.companyName || "-",
              },
              {
                label: "Payment Details: Branch Name",
                value: invoice?.paymentDetail?.branchName || "-",
              },
              {
                label: "Payment Details: Bank No",
                value: invoice?.paymentDetail?.bankNo || "-",
              },
              {
                label: "Payment Details: Swift Code",
                value: invoice?.paymentDetail?.swiftCode || "-",
              },
              {
                label: "Payment Details: Account No",
                value: invoice?.paymentDetail?.accountNo || "-",
              },
            ]}
          />

          <Heading fontSize="1.2rem" mb={2} mt={10}>
            Items
          </Heading>

          <DefaultTable
            data={invoice?.invoiceItems || []}
            columns={columns}
            getRowId={(row) => row.id as string}
          />

          <DetailLine
            mt={10}
            title="Billings"
            items={[
              {
                label: "Subtotal",
                value: <NumberFormat prefix="$ " value={invoice?.subTotal} />,
              },
              {
                label: "Eddition Costa",
                value: (
                  <NumberFormat prefix="$ " value={invoice?.additionalCost} />
                ),
              },
              {
                label: "NTA",
                value: <NumberFormat prefix="$ " value={invoice?.total} />,
              },
            ]}
          />
        </Box>
      </CardForm>
      <InvoiceForm
        isOpen={isOpenForm}
        onClose={(res) => {
          setInvoice({ ...(invoice as Invoice), ...res?.data });
          onCloseForm();
          if (res?.type === FormMutationType.UPDATE) {
            refetch();
          }
        }}
        id={invoice?.id}
      />
      <ModalFormPay onSubmit={onPay} isOpen={isOpenPay} onClose={onClosePay} />
    </>
  );
};

export default InvoiceDetail;
